import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Smile, ImageIcon, X, Clock, Send, Globe, Mic, Square, Video, Music, Activity, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { audioRecorder } from '../services/audioRecorder';
import { uploadFile } from '../services/storage';

interface FeedPostComposerProps {
  onPost: (content: string, expiresAt: number | null) => void;
  userAvatar: string;
}

export function FeedPostComposer({ onPost, userAvatar }: FeedPostComposerProps) {
  const [content, setContent] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [expiryTime, setExpiryTime] = useState<number | null>(null); // null = Permanent
  const [attachments, setAttachments] = useState<{ id: string; url?: string; type: 'image' | 'video' | 'audio'; uploading: boolean }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Emoji/GIF/Emote popover state + portal positioning
  const [pickerTab, setPickerTab] = useState<'emoji' | 'gif' | 'emotes'>('emoji');
  const [emoteSearch, setEmoteSearch] = useState('');
  const [customEmotes, setCustomEmotes] = useState<Array<{ name: string; url: string }>>([]);
  const smileButtonRef = useRef<HTMLButtonElement>(null);
  const [pickerPos, setPickerPos] = useState<{ bottom: number; left: number }>({ bottom: 0, left: 0 });

  // Fetch custom emotes once on mount (same source as ChatArea's chat picker)
  useEffect(() => {
    const FALLBACK_TW = [
      { name: 'smile', url: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f600.png' },
      { name: 'grin', url: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f601.png' },
      { name: 'joy', url: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f602.png' },
    ];
    const FALLBACK_BOT = [
      { name: 'bot1', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=1' },
      { name: 'bot2', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=2' },
      { name: 'bot3', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=3' },
      { name: 'bot4', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=4' },
    ];
    const fetchEmotes = async () => {
      try {
        const response = await fetch('https://api.github.com/repos/Stewie-J/EMOTES/contents/?ref=main');
        if (response.ok) {
          const data = await response.json();
          const list: Array<{ name: string; url: string }> = Array.isArray(data)
            ? data
                .filter((file: any) => file?.type === 'file' && /\.(png|jpg|jpeg|gif|webp)$/i.test(file?.name || ''))
                .map((file: any) => ({ name: (file.name as string).split('.')[0], url: file.download_url as string }))
            : [];
          setCustomEmotes(list.length > 0 ? list : FALLBACK_TW);
        } else {
          setCustomEmotes(FALLBACK_BOT);
        }
      } catch {
        setCustomEmotes(FALLBACK_BOT);
      }
    };
    fetchEmotes();
  }, []);

  // Popover positioning callbacks. Declared before the position useEffect so
  // the effect's closure captures them without TS2448 "used before declaration".
  const PICKER_WIDTH = 320;
  const computePickerPos = useCallback(() => {
    const btn = smileButtonRef.current;
    if (!btn) return null;
    const r = btn.getBoundingClientRect();
    return {
      bottom: Math.max(8, Math.round(window.innerHeight - r.bottom + 8)),
      left: Math.max(8, Math.min(Math.round(r.left), window.innerWidth - PICKER_WIDTH - 8)),
    };
  }, []);
  const togglePicker = useCallback(() => {
    if (showEmojiPicker) {
      setShowEmojiPicker(false);
      return;
    }
    const pos = computePickerPos();
    if (pos) setPickerPos(pos);
    setShowEmojiPicker(true);
  }, [showEmojiPicker, computePickerPos]);

  // Append a markdown snippet to the textarea content (used by gif/emote picks)
  const insertText = useCallback((text: string) => {
    setContent(prev => prev + (prev ? '\n' : '') + text);
  }, []);

  // Track the smile button's viewport rect on resize + scroll once the popover
  // is open. Initial position is computed synchronously in togglePicker so the
  // portal paints in the same render (no 1-frame "open gate" flash).
  useEffect(() => {
    if (!showEmojiPicker) return;
    const update = () => {
      const pos = computePickerPos();
      if (pos) setPickerPos(pos);
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showEmojiPicker, computePickerPos]);

  // Click outside dismisses the popover. Stays open when clicking inside the
  // smile button (it toggles itself), the textarea (user mid-pick refining
  // text alongside emoji selection), or the popover itself.
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (smileButtonRef.current?.contains(target)) return;
      if (textareaRef.current?.contains(target)) return;
      const popover = (target as Element)?.closest?.('[data-feed-emoji-popover]');
      if (!popover) setShowEmojiPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEmojiPicker]);

  const GIF_URLS = [
    'https://media.giphy.com/media/3o7TKSjRrfIPjeiKKc/giphy.gif',
    'https://media.giphy.com/media/l0HlHJGHe3yAMhdQY/giphy.gif',
    'https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif',
    'https://media.giphy.com/media/26BRv0ThflsHCqDrG/giphy.gif',
  ];

  const handlePost = () => {
    if (!content.trim() && attachments.length === 0) return;
    
    let finalContent = content.trim();
    if (attachments.length > 0) {
      const mediaMarkdown = attachments
        .filter(att => !att.uploading && att.url)
        .map(att => `![${att.type}](${att.url})`)
        .join('\n\n');
      if (mediaMarkdown) {
        finalContent += (finalContent ? '\n\n' : '') + mediaMarkdown;
      }
    }
    
    onPost(finalContent, expiryTime ? Date.now() + expiryTime : null);
    setContent('');
    setAttachments([]);
    setShowOptions(false);
    setShowEmojiPicker(false);
  };

  const handleFileUpload = async (file: File) => {
    const typeLabel = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'audio';
    const id = Math.random().toString(36).substring(7);
    
    setAttachments(prev => [...prev, { id, type: typeLabel as any, uploading: true }]);
    
    try {
      const url = await uploadFile(file);
      setAttachments(prev => prev.map(att => att.id === id ? { ...att, url, uploading: false } : att));
    } catch (err) {
      console.error(`${typeLabel} upload failed`, err);
      setAttachments(prev => prev.filter(att => att.id !== id));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleToggleRecording = async () => {
    if (isRecording) {
      const blob = await audioRecorder.stop();
      setIsRecording(false);
      
      const id = Math.random().toString(36).substring(7);
      setAttachments(prev => [...prev, { id, type: 'audio', uploading: true }]);
      
      try {
        const url = await uploadFile(blob, 'voice_message.webm');
        setAttachments(prev => prev.map(att => att.id === id ? { ...att, url, uploading: false } : att));
      } catch (err) {
        console.error("Audio upload failed", err);
        setAttachments(prev => prev.filter(att => att.id !== id));
      }
    } else {
      try {
        await audioRecorder.start();
        setIsRecording(true);
      } catch (err) {
        console.error("Failed to start recording", err);
      }
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setContent(prev => prev + emojiData.emoji);
  };

  const EXPIRY_OPTIONS = [
    { label: 'Permanent', val: null },
    { label: '24 Hours', val: 24 * 60 * 60 * 1000 },
    { label: '1 Hour', val: 60 * 60 * 1000 },
    { label: '10 Minutes', val: 10 * 60 * 1000 },
  ];

  return (
    <div 
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className="bg-white/5 border border-white/10 rounded-3xl p-4 mb-8 shadow-2xl relative group focus-within:border-[var(--neon)]/50 transition-all w-full max-w-full box-border overflow-hidden"
    >
      <div className="flex gap-4">
        <div className="w-10 h-10 rounded-2xl overflow-hidden border border-white/10 shrink-0">
          <img src={userAvatar} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind?..."
            className="w-full bg-transparent border-none outline-none text-sm text-white placeholder-white/20 resize-none min-h-[80px]"
          />

          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-3 mb-1">
              {attachments.map(att => (
                <div key={att.id} className="relative group w-24 h-24 rounded-xl overflow-hidden border border-white/10 bg-black/40 flex items-center justify-center">
                  {att.uploading ? (
                    <div className="flex flex-col items-center justify-center text-white/50">
                      <Activity size={20} className="animate-pulse mb-1 text-[var(--neon)]" />
                      <span className="text-[8px] font-bold uppercase tracking-widest">Uploading</span>
                    </div>
                  ) : att.type === 'image' ? (
                    <img src={att.url} alt="" className="w-full h-full object-cover" />
                  ) : att.type === 'video' ? (
                    <div className="w-full h-full bg-black flex items-center justify-center text-white/50">
                      <Video size={24} />
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[var(--neon)] bg-[var(--neon)]/10">
                      <Music size={24} className="mb-1" />
                      <span className="text-[8px] font-bold uppercase tracking-widest">Audio</span>
                    </div>
                  )}
                  <button 
                    onClick={() => setAttachments(prev => prev.filter(a => a.id !== att.id))}
                    className="absolute top-1 right-1 p-1 bg-black/60 rounded-full hover:bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex items-center justify-between mt-2 pt-3 border-t border-white/5">
            <div className="flex gap-2 flex-wrap">
              <button
                ref={smileButtonRef}
                onClick={togglePicker}
                className={`p-2 rounded-xl transition-all ${showEmojiPicker ? 'text-[var(--neon)] bg-white/10' : 'text-white/30 hover:text-[var(--neon)] hover:bg-white/5'}`}
              >
                <Smile size={18} className="neon-icon-glow" />
              </button>
              {showEmojiPicker && createPortal(
                <div
                  data-feed-emoji-popover
                  className="fixed z-[9999] bg-[var(--bg1)]/95 backdrop-blur-xl theme-panel border border-white/10 rounded-2xl shadow-2xl flex flex-col h-[min(55vh,450px)] w-[min(calc(100vw-1rem),320px)]"
                  style={{ bottom: pickerPos.bottom, left: pickerPos.left }}
                >
                  <div className="flex bg-black/40 p-2 gap-2 border-b border-white/10 shrink-0">
                    <button onClick={() => setPickerTab('gif')} className={`flex-1 py-1.5 text-xs font-bold rounded transition-all ${pickerTab === 'gif' ? 'bg-[var(--neon)] text-black' : 'hover:bg-white/5 text-white/50'}`}>GIFs</button>
                    <button onClick={() => setPickerTab('emotes')} className={`flex-1 py-1.5 text-xs font-bold rounded transition-all ${pickerTab === 'emotes' ? 'bg-[var(--neon)] text-black' : 'hover:bg-white/5 text-white/50'}`}>EMOTES</button>
                    <button onClick={() => setPickerTab('emoji')} className={`flex-1 py-1.5 text-xs font-bold rounded transition-all ${pickerTab === 'emoji' ? 'bg-[var(--neon)] text-black' : 'hover:bg-white/5 text-white/50'}`}>EMOJI</button>
                  </div>
                  <div className="flex-grow overflow-hidden">
                    {pickerTab === 'emoji' && <EmojiPicker theme={'dark' as any} onEmojiClick={handleEmojiClick} width="100%" height="100%" />}
                    {pickerTab === 'gif' && (
                      <div className="p-2 overflow-y-auto h-full grid grid-cols-2 gap-2">
                        {GIF_URLS.map((url, i) => (
                          <img
                            key={i}
                            src={url}
                            alt=""
                            className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-80"
                            onClick={() => insertText(`![gif](${url})`)}
                          />
                        ))}
                      </div>
                    )}
                    {pickerTab === 'emotes' && (
                      <div className="p-3 flex flex-col h-full overflow-hidden">
                        <div className="mb-3 px-1 flex items-center justify-between gap-2">
                          <h4 className="text-xs font-bold text-white/30 uppercase whitespace-nowrap">Custom Emotes</h4>
                          <div className="relative flex-1 min-w-0">
                            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/30" />
                            <input
                              type="text"
                              placeholder="Search emotes..."
                              className="w-full bg-white/5 border border-white/10 rounded-md py-1.5 pl-8 pr-2 text-xs text-white outline-none focus:border-[var(--neon)] transition-colors"
                              value={emoteSearch}
                              onChange={(e) => setEmoteSearch(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="flex-grow overflow-y-auto pr-1">
                          <div className="grid grid-cols-5 gap-2 w-full">
                            {customEmotes.length > 0
                              ? customEmotes
                                  .filter(e => e.name.toLowerCase().includes(emoteSearch.toLowerCase()))
                                  .map((emote, i) => (
                                    <div
                                      key={i}
                                      className="aspect-square bg-white/5 rounded-lg flex items-center justify-center hover:bg-white/10 cursor-pointer transition-colors group p-1.5 overflow-hidden"
                                      onClick={() => insertText(`![emote](${emote.url})`)}
                                      title={emote.name}
                                    >
                                      <img src={emote.url} className="w-full h-full object-contain" alt={emote.name} referrerPolicy="no-referrer" />
                                    </div>
                                  ))
                              : Array.from({ length: 20 }).map((_, i) => (
                                  <div key={i} className="aspect-square bg-white/5 rounded-lg flex items-center justify-center hover:bg-white/10 cursor-pointer transition-colors group">
                                    <Smile size={16} className="text-white/20 group-hover:text-[var(--neon)]" />
                                  </div>
                                ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>,
                document.body
              )}
              
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-white/30 hover:text-[var(--neon)] hover:bg-white/5 rounded-xl transition-all"
                title="Upload Media (Image/Video/Audio)"
              >
                <ImageIcon size={18} className="neon-icon-glow" />
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*,video/*,audio/*" 
                onChange={handleFileChange} 
              />

              <button 
                onClick={handleToggleRecording}
                className={`p-2 rounded-xl transition-all ${isRecording ? 'text-red-500 bg-red-500/10 animate-pulse' : 'text-white/30 hover:text-[var(--neon)] hover:bg-white/5'}`}
                title={isRecording ? "Stop Recording" : "Record Voice Message"}
              >
                {isRecording ? <Square size={18} /> : <Mic size={18} className="neon-icon-glow" />}
              </button>

              <div className="relative">
                <button 
                  onClick={() => setShowOptions(!showOptions)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${expiryTime ? 'bg-red-500/20 border-red-500/30 text-red-400' : 'bg-white/5 border-white/10 text-white/40'}`}
                >
                  <Clock size={14} className="neon-icon-glow" />
                  {expiryTime ? `${Math.floor(expiryTime / 3600000)}H TTL` : 'Permanent'}
                </button>
                
                <AnimatePresence>
                  {showOptions && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute bottom-full left-0 mb-2 w-40 bg-[var(--bg-panel)] backdrop-blur-xl border border-[var(--stroke)] rounded-xl shadow-2xl z-50 overflow-hidden"
                    >
                      {EXPIRY_OPTIONS.map(opt => (
                        <button 
                          key={opt.label}
                          onClick={() => { setExpiryTime(opt.val); setShowOptions(false); }}
                          className={`w-full text-left px-4 py-2.5 text-[10px] font-bold uppercase transition-all ${expiryTime === opt.val ? 'bg-[var(--neon)] text-black' : 'text-white/40 hover:bg-white/5 hover:text-white'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <button
              onClick={handlePost}
              disabled={!content.trim() && attachments.length === 0}
              className="arcade-post-btn text-[11px] flex items-center gap-2"
            >
              Post <Send size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
